/**
 * Advanced Lighting & Visualizer System
 * Overhauled PAR cans, lasers, moving heads, strobes, and immersive visualizer effects
 */

// ==================== COLOR UTILITIES ====================
const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
};

const rgba = (color, alpha) => {
  const a = Math.min(1, Math.max(0, alpha));
  if (color.startsWith('#')) {
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return `rgba(147, 51, 234, ${a})`;
};

const lerpColor = (c1, c2, t) => {
  const a = hexToRgb(c1), b = hexToRgb(c2);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bl = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r}, ${g}, ${bl})`;
};

// ==================== LIGHTING PRESETS ====================
export const LIGHTING_PRESETS = [
  { id: 'rave', name: 'Rave', colors: ['#9333EA', '#EC4899', '#06B6D4', '#22C55E'], speed: 1.0, intensity: 0.8, strobeRate: 0 },
  { id: 'chill', name: 'Chill', colors: ['#3B82F6', '#06B6D4', '#8B5CF6'], speed: 0.3, intensity: 0.4, strobeRate: 0 },
  { id: 'strobe', name: 'Strobe', colors: ['#FFFFFF', '#9333EA'], speed: 2.0, intensity: 1.0, strobeRate: 8 },
  { id: 'laser_show', name: 'Laser Show', colors: ['#22C55E', '#EF4444', '#3B82F6', '#FBBF24'], speed: 1.2, intensity: 0.9, strobeRate: 0 },
  { id: 'bass_drop', name: 'Bass Drop', colors: ['#DC2626', '#F97316', '#FBBF24'], speed: 1.5, intensity: 1.0, strobeRate: 2 },
  { id: 'synthwave', name: 'Synthwave', colors: ['#F72585', '#7209B7', '#3A0CA3', '#4361EE'], speed: 0.6, intensity: 0.7, strobeRate: 0 },
  { id: 'ice', name: 'Ice', colors: ['#00CED1', '#87CEEB', '#E0FFFF', '#4FC3F7'], speed: 0.4, intensity: 0.5, strobeRate: 0 },
  { id: 'fire', name: 'Fire', colors: ['#FF4500', '#FF6B00', '#FFD700', '#DC2626'], speed: 0.8, intensity: 0.8, strobeRate: 1 },
  { id: 'blackout', name: 'Blackout UV', colors: ['#7C3AED', '#4C1D95', '#2E1065'], speed: 0.5, intensity: 0.6, strobeRate: 0 },
];

// ==================== DROP EFFECT ====================
// Dramatic flash + burst when DJ triggers a "drop"
export const drawDropEffect = (ctx, W, H, dropProgress, dropColor) => {
  if (dropProgress <= 0) return;
  
  // Phase 1 (0-0.15): Blackout 
  // Phase 2 (0.15-0.35): Massive white flash + color burst
  // Phase 3 (0.35-1.0): Rapid strobe fading out with radial color waves
  
  if (dropProgress > 0.85) {
    // Blackout buildup
    const blackout = (dropProgress - 0.85) / 0.15;
    ctx.fillStyle = `rgba(0, 0, 0, ${blackout * 0.9})`;
    ctx.fillRect(0, 0, W, H);
    return;
  }
  
  if (dropProgress > 0.65) {
    // Massive flash
    const flash = (dropProgress - 0.65) / 0.2;
    const flashIntensity = Math.sin(flash * Math.PI);
    
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    
    // White flash
    ctx.fillStyle = `rgba(255, 255, 255, ${flashIntensity * 0.6})`;
    ctx.fillRect(0, 0, W, H);
    
    // Color burst from center
    const burstR = W * flash * 1.5;
    const { r, g, b } = hexToRgb(dropColor || '#9333EA');
    const burstGrad = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, burstR);
    burstGrad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${flashIntensity * 0.7})`);
    burstGrad.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${flashIntensity * 0.3})`);
    burstGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = burstGrad;
    ctx.fillRect(0, 0, W, H);
    
    ctx.restore();
    return;
  }
  
  // Rapid strobe fade-out with expanding color rings
  const fade = dropProgress / 0.65;
  const strobeOn = Math.sin(fade * 60) > 0;
  
  if (strobeOn) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const alpha = (1 - fade) * 0.25;
    const { r, g, b } = hexToRgb(dropColor || '#9333EA');
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
    ctx.fillRect(0, 0, W, H);
    
    // Expanding rings
    for (let ring = 0; ring < 4; ring++) {
      const ringR = (1 - fade + ring * 0.15) * W * 0.8;
      if (ringR > 0) {
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${(1 - fade) * 0.4})`;
        ctx.lineWidth = 4 - ring;
        ctx.beginPath();
        ctx.arc(W / 2, H / 2, ringR, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }
};

// ==================== PAR CANS ====================
const drawParCans = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  const numPars = 8;
  const parY = 15;

  for (let i = 0; i < numPars; i++) {
    const parX = (W / (numPars + 1)) * (i + 1);
    const colorIdx = i % colors.length;
    const color = colors[colorIdx];

    // PAR can body (small fixture on ceiling)
    ctx.fillStyle = '#222';
    ctx.beginPath();
    ctx.arc(parX, parY, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(color, 0.9);
    ctx.beginPath();
    ctx.arc(parX, parY + 2, 5, 0, Math.PI * 2);
    ctx.fill();

    // Beam sweeping motion - each PAR oscillates differently
    const sweepAngle = Math.sin(time * speed * 0.8 + i * 1.1) * 0.5;
    const pulsePhase = Math.sin(time * speed * 1.5 + i * 0.7);
    const beamIntensity = (0.15 + pulsePhase * 0.08 + audioLevel * 0.12) * intensity;
    
    // The beam target sweeps left/right
    const targetX = parX + Math.sin(time * speed * 0.6 + i * 0.9) * (W * 0.08);
    const targetY = H * (0.5 + Math.sin(time * speed * 0.3 + i * 1.3) * 0.15);
    const beamWidth = 35 + audioLevel * 20;

    // Draw volumetric cone beam
    const grad = ctx.createLinearGradient(parX, parY, targetX, targetY);
    grad.addColorStop(0, rgba(color, beamIntensity * 1.2));
    grad.addColorStop(0.6, rgba(color, beamIntensity * 0.4));
    grad.addColorStop(1, 'transparent');

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(parX - 4, parY);
    ctx.lineTo(parX + 4, parY);
    ctx.lineTo(targetX + beamWidth, targetY);
    ctx.lineTo(targetX - beamWidth, targetY);
    ctx.closePath();
    ctx.fill();

    // Floor hit glow
    const floorGlow = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, beamWidth * 1.5);
    floorGlow.addColorStop(0, rgba(color, beamIntensity * 0.35));
    floorGlow.addColorStop(1, 'transparent');
    ctx.fillStyle = floorGlow;
    ctx.beginPath();
    ctx.ellipse(targetX, targetY, beamWidth * 1.5, beamWidth * 0.5, sweepAngle * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
};

// ==================== MOVING HEAD SPOTLIGHTS ====================
const drawMovingHeads = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  const heads = [
    { x: W * 0.15, baseAngle: 0 },
    { x: W * 0.35, baseAngle: Math.PI * 0.5 },
    { x: W * 0.65, baseAngle: Math.PI },
    { x: W * 0.85, baseAngle: Math.PI * 1.5 },
  ];

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  heads.forEach((head, i) => {
    const color = colors[i % colors.length];
    const headY = 10;

    // Complex sweep pattern - figure-8 motion
    const sweepX = Math.sin(time * speed * 0.4 + head.baseAngle) * W * 0.2;
    const sweepY = Math.cos(time * speed * 0.3 + head.baseAngle * 0.7) * H * 0.15;
    const targetX = W * 0.5 + sweepX;
    const targetY = H * 0.55 + sweepY;

    const beamAlpha = (0.12 + audioLevel * 0.15) * intensity;
    const beamW = 25 + audioLevel * 15;

    // Sharp beam
    const grad = ctx.createLinearGradient(head.x, headY, targetX, targetY);
    grad.addColorStop(0, rgba(color, beamAlpha));
    grad.addColorStop(0.7, rgba(color, beamAlpha * 0.3));
    grad.addColorStop(1, 'transparent');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(head.x - 3, headY);
    ctx.lineTo(head.x + 3, headY);
    ctx.lineTo(targetX + beamW, targetY);
    ctx.lineTo(targetX - beamW, targetY);
    ctx.closePath();
    ctx.fill();

    // Spotlight circle on ground
    const spotGrad = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, beamW * 2);
    spotGrad.addColorStop(0, rgba(color, beamAlpha * 0.5));
    spotGrad.addColorStop(0.5, rgba(color, beamAlpha * 0.15));
    spotGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = spotGrad;
    ctx.beginPath();
    ctx.ellipse(targetX, targetY, beamW * 2, beamW * 0.7, 0, 0, Math.PI * 2);
    ctx.fill();

    // Fixture body
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(head.x, headY, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(color, 0.7);
    ctx.beginPath();
    ctx.arc(head.x, headY + 1, 4, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.restore();
};

// ==================== LASER SYSTEM ====================
const drawLasers = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';

  // Central laser origin point (like a real laser projector above DJ)
  const laserX = W * 0.5;
  const laserY = 25;
  const numBeams = 12;

  for (let i = 0; i < numBeams; i++) {
    const color = colors[i % colors.length];
    const t = time * speed;
    
    // Fan pattern that opens/closes with audio
    const fanWidth = (0.8 + audioLevel * 0.6) * Math.PI;
    const baseAngle = -Math.PI / 2; // pointing down
    const spreadAngle = ((i / (numBeams - 1)) - 0.5) * fanWidth;
    
    // Add wobble to each beam
    const wobble = Math.sin(t * 1.2 + i * 0.8) * 0.15;
    const angle = baseAngle + spreadAngle + wobble;
    
    // Beam length varies with audio
    const beamLen = (H * 0.7 + audioLevel * H * 0.2);
    const endX = laserX + Math.cos(angle) * beamLen;
    const endY = laserY + Math.sin(angle) * beamLen;

    const alpha = (0.3 + audioLevel * 0.4) * intensity;

    // Glow layer
    ctx.strokeStyle = rgba(color, alpha * 0.3);
    ctx.lineWidth = 6;
    ctx.shadowColor = color;
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.moveTo(laserX, laserY);
    ctx.lineTo(endX, Math.min(endY, H - 10));
    ctx.stroke();

    // Core beam
    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = 2;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(laserX, laserY);
    ctx.lineTo(endX, Math.min(endY, H - 10));
    ctx.stroke();
  }

  // Horizontal scanning lasers (2 beams that sweep)
  for (let j = 0; j < 2; j++) {
    const color = colors[(j + 2) % colors.length];
    const scanY = H * 0.25 + j * H * 0.2 + Math.sin(time * speed * 0.5 + j * 2) * 30;
    const scanProgress = (Math.sin(time * speed * 0.8 + j * Math.PI) + 1) * 0.5;
    const scanX = scanProgress * W;
    
    const alpha = (0.2 + audioLevel * 0.3) * intensity;
    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(0, scanY);
    ctx.lineTo(W, scanY + Math.sin(time * speed) * 20);
    ctx.stroke();

    // Scan dot
    ctx.fillStyle = rgba(color, alpha * 2);
    ctx.beginPath();
    ctx.arc(scanX, scanY + Math.sin(time * speed) * 10, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.shadowBlur = 0;
  ctx.restore();
};

// ==================== STROBE EFFECT ====================
const drawStrobe = (ctx, W, H, time, preset, audioLevel) => {
  if (preset.strobeRate <= 0) return;
  
  const strobePhase = Math.sin(time * preset.strobeRate * 3);
  if (strobePhase > 0.85) {
    const flashIntensity = (strobePhase - 0.85) * 6.67 * preset.intensity * 0.25;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = rgba('#FFFFFF', Math.min(flashIntensity, 0.3));
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }
};

// ==================== DISCO BALL ====================
const drawDiscoBall = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  const ballX = W * 0.5;
  const ballY = 30;
  const ballR = 16;

  // Ball body
  ctx.save();
  const ballGrad = ctx.createRadialGradient(ballX - 3, ballY - 3, 2, ballX, ballY, ballR);
  ballGrad.addColorStop(0, '#e0e0e0');
  ballGrad.addColorStop(0.5, '#a0a0a0');
  ballGrad.addColorStop(1, '#606060');
  ctx.fillStyle = ballGrad;
  ctx.beginPath();
  ctx.arc(ballX, ballY, ballR, 0, Math.PI * 2);
  ctx.fill();

  // Facets
  for (let row = -2; row <= 2; row++) {
    for (let col = 0; col < 8; col++) {
      const angle = (col / 8) * Math.PI * 2 + time * speed * 0.5;
      const fy = ballY + row * 5;
      const fx = ballX + Math.cos(angle) * (ballR - 3) * Math.cos(row * 0.5);
      if (Math.sqrt((fx - ballX) ** 2 + (fy - ballY) ** 2) < ballR - 2) {
        const brightness = (Math.sin(angle + time * speed) + 1) * 0.5;
        ctx.fillStyle = rgba('#fff', 0.3 + brightness * 0.5);
        ctx.fillRect(fx - 1.5, fy - 1.5, 3, 3);
      }
    }
  }

  // Reflection beams shooting outward
  ctx.globalCompositeOperation = 'lighter';
  const numBeams = 10;
  for (let i = 0; i < numBeams; i++) {
    const angle = (i / numBeams) * Math.PI * 2 + time * speed * 0.6;
    const len = H * 0.4 + audioLevel * H * 0.2;
    const endX = ballX + Math.cos(angle) * len;
    const endY = ballY + Math.sin(angle) * len * 0.6 + len * 0.3;
    const color = colors[i % colors.length];
    const alpha = (0.04 + audioLevel * 0.06) * intensity;

    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(ballX, ballY);
    ctx.lineTo(endX, Math.min(endY, H));
    ctx.stroke();

    // Small spot where beam hits
    if (endY < H) {
      ctx.fillStyle = rgba(color, alpha * 2);
      ctx.beginPath();
      ctx.arc(endX, Math.min(endY, H - 5), 5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
};

// ==================== NEON WALL TUBES ====================
const drawNeonTubes = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;

  const tubes = [
    // Left wall
    { x1: 8, y1: H * 0.1, x2: 8, y2: H * 0.85, colorIdx: 0 },
    { x1: 20, y1: H * 0.15, x2: 20, y2: H * 0.8, colorIdx: 1 },
    // Right wall
    { x1: W - 8, y1: H * 0.1, x2: W - 8, y2: H * 0.85, colorIdx: 0 },
    { x1: W - 20, y1: H * 0.15, x2: W - 20, y2: H * 0.8, colorIdx: 1 },
    // Top frame
    { x1: 25, y1: 8, x2: W - 25, y2: 8, colorIdx: 2 % colors.length },
  ];

  tubes.forEach(({ x1, y1, x2, y2, colorIdx }) => {
    const color = colors[colorIdx % colors.length];
    const pulse = (Math.sin(time * speed * 0.6 + colorIdx * 1.2) + 1) * 0.5;
    const alpha = (0.15 + pulse * 0.15 + audioLevel * 0.15) * intensity;

    // Glow
    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Core
    ctx.strokeStyle = rgba('#fff', alpha * 0.5);
    ctx.lineWidth = 2;
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.shadowBlur = 0;
  });
};

// ==================== FLOOR TILES (reactive dance floor) ====================
const drawReactiveFloor = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  const floorY = H * 0.78;
  const tileSize = 42;
  const cols = Math.ceil(W / tileSize);
  const rows = 3;

  ctx.save();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const wave = Math.sin(time * speed * 0.8 + c * 0.5 + r * 0.4);
      const beat = audioLevel > 0.4 ? (audioLevel - 0.4) * 1.5 : 0;
      const brightness = (0.06 + (wave * 0.5 + 0.5) * 0.12 + beat * 0.15) * intensity;
      const color = colors[(c + r) % colors.length];

      ctx.fillStyle = rgba(color, brightness);
      ctx.fillRect(c * tileSize + 2, floorY + r * tileSize + 2, tileSize - 4, tileSize - 4);
    }
  }
  ctx.restore();
};

// ==================== FLOOR FOG ====================
const drawFloorFog = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  const fogY = H * 0.75;

  // Layered fog clouds drifting across the floor
  for (let i = 0; i < 6; i++) {
    const x = ((time * speed * 15 + i * 200) % (W + 200)) - 100;
    const y = fogY + Math.sin(time * speed * 0.3 + i) * 10 + i * 8;
    const w = 180 + Math.sin(i * 2.3) * 60;
    const h = 25 + Math.sin(time * speed * 0.5 + i * 1.7) * 8;
    const alpha = (0.06 + audioLevel * 0.04) * intensity;

    const fogGrad = ctx.createRadialGradient(x + w / 2, y, 0, x + w / 2, y, w / 2);
    fogGrad.addColorStop(0, rgba(colors[i % colors.length], alpha));
    fogGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = fogGrad;
    ctx.beginPath();
    ctx.ellipse(x + w / 2, y, w / 2, h, 0, 0, Math.PI * 2);
    ctx.fill();
  }
};

// ==================== SCANNER BEAMS (criss-cross ceiling scanners) ====================
const drawScannerBeams = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  const scanners = [
    { x: 0, dir: 1 },
    { x: W, dir: -1 },
  ];

  scanners.forEach((sc, si) => {
    for (let b = 0; b < 3; b++) {
      const color = colors[(si + b) % colors.length];
      const angle = Math.sin(time * speed * 0.5 + si * Math.PI + b * 0.7) * 0.6;
      const beamLen = H * 1.2;
      const startY = 5;
      const endX = sc.x + Math.cos(-Math.PI / 2 + angle * sc.dir) * beamLen;
      const endY = startY + Math.sin(-Math.PI / 2 + angle * sc.dir) * beamLen * sc.dir * -1;
      const alpha = (0.08 + audioLevel * 0.1) * intensity;

      ctx.strokeStyle = rgba(color, alpha);
      ctx.lineWidth = 3;
      ctx.shadowColor = color;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(sc.x, startY);
      ctx.lineTo(endX, Math.abs(endY));
      ctx.stroke();
    }
  });
  ctx.shadowBlur = 0;
  ctx.restore();
};

// ==================== UV WASH (blacklight glow on walls/floor) ====================
const drawUVWash = (ctx, W, H, time, preset, audioLevel) => {
  const { intensity } = preset;
  const uvColor = '#7C3AED';
  const pulse = (Math.sin(time * 0.5) + 1) * 0.5;
  const alpha = (0.04 + pulse * 0.03 + audioLevel * 0.03) * intensity;

  // Top UV strip
  const topGrad = ctx.createLinearGradient(0, 0, 0, H * 0.25);
  topGrad.addColorStop(0, rgba(uvColor, alpha * 2));
  topGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = topGrad;
  ctx.fillRect(0, 0, W, H * 0.25);

  // Side UV strips
  const leftGrad = ctx.createLinearGradient(0, 0, W * 0.1, 0);
  leftGrad.addColorStop(0, rgba(uvColor, alpha * 1.5));
  leftGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = leftGrad;
  ctx.fillRect(0, 0, W * 0.15, H);

  const rightGrad = ctx.createLinearGradient(W, 0, W * 0.9, 0);
  rightGrad.addColorStop(0, rgba(uvColor, alpha * 1.5));
  rightGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = rightGrad;
  ctx.fillRect(W * 0.85, 0, W * 0.15, H);
};

// ==================== CONFETTI / SPARK PARTICLES ====================
const drawConfettiParticles = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  if (audioLevel < 0.3) return; // Only show during active beats
  
  const numParticles = Math.floor(30 * audioLevel);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  
  for (let i = 0; i < numParticles; i++) {
    const seed = i * 173.7;
    const px = ((seed + time * speed * 40) % W);
    const py = ((seed * 0.7 + time * speed * 80 + i * 31) % (H * 0.8)) + H * 0.1;
    const size = 2 + (seed % 3);
    const color = colors[i % colors.length];
    const sparkle = (Math.sin(time * 5 + i * 2.1) + 1) * 0.5;
    const alpha = sparkle * 0.6 * intensity * audioLevel;

    ctx.fillStyle = rgba(color, alpha);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(time * speed * 2 + i);
    ctx.fillRect(-size / 2, -size / 2, size, size * 0.5);
    ctx.restore();
  }
  ctx.restore();
};

// ==================== CEILING PLASMA (ambient wash effect) ====================
const drawCeilingPlasma = (ctx, W, H, time, preset, audioLevel) => {
  const { colors, speed, intensity } = preset;
  const cellSize = 50;
  const rows = Math.ceil(H * 0.2 / cellSize);
  const cols = Math.ceil(W / cellSize);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = c * cellSize;
      const y = r * cellSize;
      const val = Math.sin(x * 0.02 + time * speed * 0.8) * Math.cos(y * 0.03 - time * speed * 0.5) +
        Math.sin((x + y) * 0.015 + time * speed * 0.4);
      const n = (val + 2) / 4;
      const ci = Math.floor(n * colors.length) % colors.length;
      const alpha = n * 0.08 * intensity * (0.6 + audioLevel * 0.4);
      ctx.fillStyle = rgba(colors[ci], alpha);
      ctx.fillRect(x, y, cellSize, cellSize);
    }
  }
};

// ==================== MAIN ROOM LIGHTING (COMPLETE) ====================
export const drawMainRoomLighting = (ctx, W, H, phase, audioLevel, presetId, lightingFx) => {
  ctx.save();
  const time = phase * 0.016; // ~60fps normalized time
  const preset = LIGHTING_PRESETS.find(p => p.id === presetId) || LIGHTING_PRESETS[0];
  const audio = Math.min(audioLevel, 1);

  drawDiscoBall(ctx, W, H, time, preset, audio);
  drawCeilingPlasma(ctx, W, H, time, preset, audio);
  drawParCans(ctx, W, H, time, preset, audio);
  drawMovingHeads(ctx, W, H, time, preset, audio);
  drawLasers(ctx, W, H, time, preset, audio);
  drawScannerBeams(ctx, W, H, time, preset, audio);
  drawNeonTubes(ctx, W, H, time, preset, audio);
  drawReactiveFloor(ctx, W, H, time, preset, audio);
  drawFloorFog(ctx, W, H, time, preset, audio);
  drawUVWash(ctx, W, H, time, preset, audio);
  drawConfettiParticles(ctx, W, H, time, preset, audio);
  drawStrobe(ctx, W, H, time, preset, audio);

  // Vignette - must be in source-over mode
  ctx.globalCompositeOperation = 'source-over';
  const vignette = ctx.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, H);
  vignette.addColorStop(0, 'transparent');
  vignette.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
};


// ==================== VISUALIZER ROOM ====================

const drawVisualizerWallBars = (ctx, W, wallH, wallY, time, colors, audioMod, intensity) => {
  const numBars = 48;
  const barW = (W - 60) / numBars;
  
  for (let i = 0; i < numBars; i++) {
    const x = 30 + i * barW;
    // Multi-frequency sine combination for organic motion
    const h1 = Math.sin(time * 1.2 + i * 0.25) * 0.5 + 0.5;
    const h2 = Math.sin(time * 0.8 + i * 0.15 + 1) * 0.3 + 0.3;
    const barH = (h1 + h2) * wallH * 0.35 * (0.3 + audioMod * 0.7) * intensity;
    const color = colors[Math.floor((i / numBars) * colors.length) % colors.length];

    // Bar with gradient
    const grad = ctx.createLinearGradient(x, wallY - barH, x, wallY);
    grad.addColorStop(0, color);
    grad.addColorStop(0.5, rgba(color, 0.6));
    grad.addColorStop(1, rgba(color, 0.1));
    ctx.fillStyle = grad;
    ctx.fillRect(x, wallY - barH, barW * 0.75, barH);

    // Mirror on right side (mirrored bars)
    const mirrorX = W - 30 - (i + 1) * barW;
    ctx.fillStyle = grad;
    ctx.fillRect(mirrorX, wallY - barH * 0.8, barW * 0.75, barH * 0.8);

    // Glow cap
    ctx.fillStyle = rgba(color, 0.7 * intensity);
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.fillRect(x, wallY - barH - 3, barW * 0.75, 3);
    ctx.shadowBlur = 0;
  }
};

const drawVisualizerWaveform = (ctx, W, wallH, wallY, time, colors, audioMod, intensity) => {
  // Multiple layered waveforms
  for (let layer = 0; layer < 3; layer++) {
    const color = colors[layer % colors.length];
    const layerOffset = layer * 0.3;
    
    ctx.strokeStyle = rgba(color, (0.6 - layer * 0.15) * intensity);
    ctx.lineWidth = 3 - layer * 0.5;
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    
    for (let x = 0; x <= W; x += 3) {
      const y = wallY - wallH * 0.4 +
        Math.sin(x * 0.015 + time * 1.3 + layerOffset) * wallH * 0.15 * (0.4 + audioMod * 0.6) * intensity +
        Math.sin(x * 0.008 + time * 0.7 + layerOffset * 2) * wallH * 0.1 * intensity;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
};

const drawVisualizerCircularSpectrum = (ctx, W, wallH, wallY, time, colors, audioMod, intensity) => {
  const cx = W / 2;
  const cy = wallY - wallH * 0.45;
  const baseR = 60;
  const segments = 80;

  // Rotating outer ring
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(time * 0.2);

  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const freq = Math.sin(time * 1.0 + i * 0.2) * 0.5 + 0.5;
    const segH = freq * 50 * (0.3 + audioMod * 0.7) * intensity;
    const color = colors[Math.floor((i / segments) * colors.length) % colors.length];

    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.shadowColor = color;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.moveTo(Math.cos(angle) * baseR, Math.sin(angle) * baseR);
    ctx.lineTo(Math.cos(angle) * (baseR + segH), Math.sin(angle) * (baseR + segH));
    ctx.stroke();
  }

  // Inner pulsing core
  const coreR = baseR * 0.6 + audioMod * 10;
  const coreGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
  coreGrad.addColorStop(0, rgba(colors[0], 0.4 * intensity));
  coreGrad.addColorStop(0.7, rgba(colors[1 % colors.length], 0.15 * intensity));
  coreGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = coreGrad;
  ctx.beginPath();
  ctx.arc(0, 0, coreR, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.restore();
};

const drawVisualizerTunnel = (ctx, W, wallH, wallY, time, colors, audioMod, intensity) => {
  const cx = W / 2;
  const cy = wallY - wallH * 0.4;
  const maxRings = 12;

  for (let ring = maxRings; ring >= 0; ring--) {
    const baseR = 20 + ring * 20;
    const pulseR = baseR + Math.sin(time * 1.5 - ring * 0.6) * 12 * audioMod;
    const depth = ring / maxRings;
    const alpha = (1 - depth * 0.7) * intensity * 0.6;
    const color = colors[ring % colors.length];

    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = 2 + (1 - depth) * 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 8 * (1 - depth);

    // Slightly rotating hexagonal rings
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(time * 0.3 + ring * 0.1);
    
    ctx.beginPath();
    const sides = 6;
    for (let s = 0; s <= sides; s++) {
      const angle = (s / sides) * Math.PI * 2;
      const x = Math.cos(angle) * pulseR;
      const y = Math.sin(angle) * pulseR * 0.7;
      if (s === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.restore();
  }
  ctx.shadowBlur = 0;
};

const drawVisualizerParticleField = (ctx, W, wallH, wallY, time, colors, audioMod, intensity) => {
  const numParticles = 80;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  for (let i = 0; i < numParticles; i++) {
    // Orbiting particles with varying speeds
    const orbitSpeed = 0.3 + (i % 5) * 0.15;
    const orbitR = 30 + (i * 7.3) % 200;
    const baseX = W / 2 + Math.sin(time * orbitSpeed + i * 0.8) * orbitR;
    const baseY = wallY - wallH * 0.4 + Math.cos(time * orbitSpeed * 0.7 + i * 1.1) * (wallH * 0.3);
    const size = 2 + Math.sin(time * 2 + i) * 1.5 * audioMod;
    const color = colors[i % colors.length];
    const alpha = (0.4 + Math.sin(time * 1.5 + i * 0.5) * 0.2) * intensity;

    ctx.fillStyle = rgba(color, alpha);
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(baseX, baseY, size, 0, Math.PI * 2);
    ctx.fill();

    // Trailing line
    if (i % 3 === 0) {
      const prevX = W / 2 + Math.sin(time * orbitSpeed - 0.1 + i * 0.8) * orbitR;
      const prevY = wallY - wallH * 0.4 + Math.cos(time * orbitSpeed * 0.7 - 0.1 + i * 1.1) * (wallH * 0.3);
      ctx.strokeStyle = rgba(color, alpha * 0.4);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(prevX, prevY);
      ctx.lineTo(baseX, baseY);
      ctx.stroke();
    }
  }
  ctx.shadowBlur = 0;
  ctx.restore();
};

const drawVisualizerFloorRipple = (ctx, W, H, floorY, time, colors, audioMod, intensity) => {
  const cx = W / 2;
  const cy = (floorY + H) / 2;

  for (let ring = 0; ring < 12; ring++) {
    const r = ((time * 60 + ring * 35) % 350) * intensity;
    const life = Math.max(0, 1 - r / 350);
    const alpha = life * 0.5 * audioMod * intensity;
    const color = colors[ring % colors.length];

    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = 2 + life * 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 6 * life;
    ctx.beginPath();
    ctx.ellipse(cx, cy, r, r * 0.35, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
};

const drawVisualizerCeilingLasers = (ctx, W, floorY, time, colors, audioMod, intensity) => {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  const origins = [
    { x: W * 0.2, y: 15 },
    { x: W * 0.5, y: 10 },
    { x: W * 0.8, y: 15 },
  ];

  origins.forEach((origin, oi) => {
    const beamCount = 8;
    for (let i = 0; i < beamCount; i++) {
      const color = colors[(oi + i) % colors.length];
      const fanAngle = ((i / (beamCount - 1)) - 0.5) * (0.7 + audioMod * 0.5);
      const wobble = Math.sin(time * 1.5 + oi * 2 + i * 0.6) * 0.12;
      const angle = -Math.PI / 2 + fanAngle + wobble;
      const len = (floorY - 30) * (0.8 + audioMod * 0.2);
      const endX = origin.x + Math.cos(angle) * len;
      const endY = origin.y + Math.sin(angle) * len;

      const alpha = (0.25 + audioMod * 0.3) * intensity;
      ctx.strokeStyle = rgba(color, alpha);
      ctx.lineWidth = 2;
      ctx.shadowColor = color;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(origin.x, origin.y);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
  });

  ctx.shadowBlur = 0;
  ctx.restore();
};

const drawVisualizerNebula = (ctx, W, floorY, time, colors, audioMod, intensity) => {
  const numClouds = 20;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  for (let i = 0; i < numClouds; i++) {
    const cx = (Math.sin(time * 0.15 + i * 2.1) + 1) * 0.5 * W;
    const cy = 30 + (Math.cos(time * 0.12 + i * 1.7) + 1) * 0.5 * (floorY - 60);
    const size = 40 + Math.sin(time * 0.5 + i * 0.8) * 20 + audioMod * 15;
    const color = colors[i % colors.length];
    const alpha = (0.08 + audioMod * 0.06) * intensity;

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size);
    grad.addColorStop(0, rgba(color, alpha * 1.5));
    grad.addColorStop(0.5, rgba(color, alpha * 0.5));
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
};

// ==================== VISUALIZER ROOM (COMPLETE) ====================
export const drawAdvancedVisualizerRoom = (ctx, W, H, floorY, phase, audioLevel, settings) => {
  ctx.save();
  const time = phase * 0.016;
  const colorScheme = settings.colorScheme || 'neon';
  const intensity = settings.intensity || 0.7;
  const audioMod = settings.syncToAudio !== false ? Math.min(audioLevel, 1) : 0.5;

  // Color palettes
  const palettes = {
    neon: ['#FF00FF', '#00FFFF', '#FF0080', '#39FF14'],
    fire: ['#FF4500', '#FF8C00', '#FFD700', '#DC2626'],
    ice: ['#00CED1', '#87CEEB', '#E0FFFF', '#4FC3F7'],
    rainbow: ['#FF0000', '#FF8C00', '#FFFF00', '#00FF00', '#0000FF', '#8B00FF'],
    vaporwave: ['#FF6AD5', '#8B5CF6', '#06B6D4', '#C084FC'],
    synthwave: ['#F72585', '#7209B7', '#3A0CA3', '#4361EE'],
  };
  const colors = palettes[colorScheme] || palettes.neon;

  const wallY = floorY - 10;
  const wallH = wallY * 0.8;

  // === WALL VISUALS ===
  switch (settings.wallVisual) {
    case 'bars':
      drawVisualizerWallBars(ctx, W, wallH, wallY, time, colors, audioMod, intensity);
      break;
    case 'wave':
      drawVisualizerWaveform(ctx, W, wallH, wallY, time, colors, audioMod, intensity);
      break;
    case 'circular':
      drawVisualizerCircularSpectrum(ctx, W, wallH, wallY, time, colors, audioMod, intensity);
      break;
    case 'tunnel':
      drawVisualizerTunnel(ctx, W, wallH, wallY, time, colors, audioMod, intensity);
      break;
    case 'particles':
      drawVisualizerParticleField(ctx, W, wallH, wallY, time, colors, audioMod, intensity);
      break;
    case 'kaleidoscope': {
      const kcx = W / 2, kcy = wallY - wallH * 0.4;
      const sides = 10;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let s = 0; s < sides; s++) {
        ctx.save();
        ctx.translate(kcx, kcy);
        ctx.rotate((s / sides) * Math.PI * 2 + time * 0.3);
        const grad = ctx.createLinearGradient(0, -120, 60, 120);
        grad.addColorStop(0, rgba(colors[s % colors.length], 0.5 * intensity));
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        const armLen = 100 + audioMod * 40;
        ctx.lineTo(armLen, -40 + Math.sin(time + s) * 20);
        ctx.lineTo(armLen * 1.1, 0);
        ctx.lineTo(armLen, 40 + Math.cos(time + s) * 20);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();
      break;
    }
    case 'matrix':
      ctx.font = '14px monospace';
      for (let col = 0; col < W / 18; col++) {
        const x = col * 18 + 9;
        const offset = (time * 120 + col * 137) % (wallY - 20);
        for (let row = 0; row < 10; row++) {
          const y = offset - row * 18;
          if (y > 20 && y < wallY - 20) {
            const alpha = Math.max(0, 1 - row * 0.12) * intensity;
            ctx.fillStyle = `rgba(57, 255, 20, ${alpha})`;
            ctx.fillText(String.fromCharCode(0x30A0 + ((col * 13 + row * 7 + Math.floor(time * 5)) % 96)), x, y);
          }
        }
      }
      break;
    default:
      drawVisualizerWallBars(ctx, W, wallH, wallY, time, colors, audioMod, intensity);
  }

  // === FLOOR VISUALS ===
  switch (settings.floorVisual) {
    case 'ripple':
      drawVisualizerFloorRipple(ctx, W, H, floorY, time, colors, audioMod, intensity);
      break;
    case 'fire':
      for (let fx = 0; fx < W; fx += 15) {
        const flameH = 15 + (Math.sin(time * 3 + fx * 0.1) + 1) * 20 * audioMod * intensity;
        const grad = ctx.createLinearGradient(fx, floorY + 25, fx, floorY + 25 - flameH);
        grad.addColorStop(0, '#FF4500');
        grad.addColorStop(0.4, '#FF8C00');
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(fx, floorY + 25);
        ctx.quadraticCurveTo(fx + 7.5 + Math.sin(time * 2 + fx * 0.15) * 4, floorY + 25 - flameH, fx + 15, floorY + 25);
        ctx.fill();
      }
      break;
    case 'tiles': {
      const ts = 55;
      for (let tx = 0; tx < W; tx += ts) {
        for (let ty = floorY; ty < H; ty += ts) {
          const phase2 = Math.sin(time * 1.2 + tx * 0.04 + ty * 0.03);
          if (phase2 > 0.2 * (1 - audioMod)) {
            const ci = Math.floor((tx / ts + ty / ts) % colors.length);
            const alpha = phase2 * 0.4 * intensity;
            ctx.fillStyle = rgba(colors[ci], alpha);
            ctx.fillRect(tx + 2, ty + 2, ts - 4, ts - 4);
          }
        }
      }
      break;
    }
    case 'pulse': {
      const pts = [{ x: W * 0.25, y: (floorY + H) * 0.5 }, { x: W * 0.75, y: (floorY + H) * 0.5 }, { x: W * 0.5, y: floorY + 25 }];
      pts.forEach((p, pi) => {
        for (let ring = 0; ring < 6; ring++) {
          const r = ((time * 80 + ring * 25 + pi * 60) % 180) * intensity;
          const alpha = Math.max(0, 1 - r / 180) * audioMod * 0.6;
          ctx.strokeStyle = rgba(colors[pi % colors.length], alpha);
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.stroke();
        }
      });
      break;
    }
    default: {
      // Grid default
      const gs = 38;
      for (let gx = 0; gx < W; gx += gs) {
        for (let gy = floorY; gy < H; gy += gs) {
          const d = Math.sqrt((gx - W / 2) ** 2 + (gy - floorY - (H - floorY) / 2) ** 2);
          const b = (Math.sin(time * 1.2 - d * 0.02) + 1) * 0.5 * audioMod * intensity * 0.4;
          const ci = Math.floor((gx / W) * colors.length);
          ctx.fillStyle = rgba(colors[ci % colors.length], b);
          ctx.fillRect(gx + 2, gy + 2, gs - 4, gs - 4);
        }
      }
    }
  }

  // === CEILING VISUALS ===
  switch (settings.ceilingVisual) {
    case 'lasers':
      drawVisualizerCeilingLasers(ctx, W, floorY, time, colors, audioMod, intensity);
      break;
    case 'nebula':
      drawVisualizerNebula(ctx, W, floorY, time, colors, audioMod, intensity);
      break;
    case 'plasma':
      for (let px = 0; px < W; px += 35) {
        for (let py = 20; py < floorY - 20; py += 35) {
          const val = Math.sin(px * 0.018 + time * 1.2) * Math.cos(py * 0.018 - time * 0.8) +
            Math.sin((px + py) * 0.012 + time * 0.6);
          const n = (val + 2) / 4;
          const ci = Math.floor(n * colors.length);
          ctx.fillStyle = rgba(colors[ci % colors.length], n * 0.25 * intensity);
          ctx.fillRect(px, py, 33, 33);
        }
      }
      break;
    default:
      // Stars default
      for (let i = 0; i < 100; i++) {
        const sx = (i * 137.5) % W;
        const sy = 20 + (i * 73.7) % (floorY - 40);
        const twinkle = (Math.sin(time * 1.5 + i * 1.1) + 1) * 0.5;
        const alpha = twinkle * 0.6 * intensity;
        ctx.fillStyle = rgba(colors[i % colors.length], alpha);
        ctx.beginPath();
        ctx.arc(sx, sy, 1 + twinkle * 2, 0, Math.PI * 2);
        ctx.fill();
      }
  }

  // Ambient glow overlay
  const ambGrad = ctx.createRadialGradient(W / 2, H * 0.4, 0, W / 2, H * 0.4, W * 0.6);
  ambGrad.addColorStop(0, rgba(colors[0], 0.03 * intensity));
  ambGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = ambGrad;
  ctx.fillRect(0, 0, W, H);

  // Room label
  ctx.font = 'bold 16px sans-serif';
  ctx.fillStyle = colors[0];
  ctx.textAlign = 'left';
  ctx.shadowColor = colors[0];
  ctx.shadowBlur = 10;
  ctx.fillText('VISUALIZER - ' + (settings.wallVisual || 'BARS').toUpperCase(), 20, 80);
  ctx.shadowBlur = 0;
  ctx.restore();
};
